import React, { useEffect, useMemo, useState } from 'react'
import { Button } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { FiEye, FiEyeOff } from 'react-icons/fi'
import { closeConnectionModal, fetchAllPublicKeys, deletePublicKey } from '../../integrationsSlice'
import { fetchAllSecrets } from '../../../../features/aws/awsSecretsManagerSlice'
import { baseUrl } from '../../../../modules/urls'
import { getAxiosDefaultConfig } from '../../../../modules/utils'
import { parseIntegrationTags } from '../../integrationTags'

const makeswiftConnectUrl = `${baseUrl}/auth/makeswift/connect`

interface FormValues {
  apiKey: string
}

interface Channel {
  id: string
  ppKeys?: Array<{ keyId: string; keyValue: string; tags?: unknown }>
}

interface Props {
  ownerId: string | number
  retrieveAccounts: () => Promise<void>
  channel?: Channel
  allPublicKeys?: Array<{ keyId: string; keyValue?: string; tags?: unknown }>
}

type ViewMode = 'manage' | 'connect' | 'update'

function isMakeswiftKeyId(keyId: string | undefined | null): boolean {
  if (keyId == null || keyId === '') return false
  const parts = String(keyId).split('--')
  return parts.length === 2 && parts[1] === 'makeswift'
}

function collectMakeswiftRows(
  channel: Channel | undefined,
  allPublicKeys: Array<{ keyId: string; tags?: unknown }> | undefined
): Array<{ keyId: string; key_preview: string }> {
  const map = new Map<string, { keyId: string; tags?: unknown }>()
  ;(allPublicKeys || []).forEach((row) => {
    if (!row?.keyId || !isMakeswiftKeyId(row.keyId)) return
    map.set(row.keyId, row)
  })
  ;(channel?.ppKeys || []).forEach((row: any) => {
    if (row?.keyId && isMakeswiftKeyId(row.keyId) && !map.has(row.keyId)) {
      map.set(row.keyId, row)
    }
  })
  return Array.from(map.values()).map((row) => {
    const tm = parseIntegrationTags(row.tags)
    return {
      keyId: row.keyId,
      key_preview: String(tm.makeswift_api_key_preview || ''),
    }
  })
}

export default function MakeswiftConnectionModalContent({
  ownerId,
  retrieveAccounts,
  channel,
  allPublicKeys,
}: Props) {
  const dispatch = useDispatch<any>()
  const closeModal = () => dispatch((closeConnectionModal as any)())

  const rows = useMemo(() => collectMakeswiftRows(channel, allPublicKeys), [channel, allPublicKeys])
  const hasConnections = rows.length > 0

  const [viewMode, setViewMode] = useState<ViewMode>(hasConnections ? 'manage' : 'connect')
  const [showKey, setShowKey] = useState(false)
  const [connectError, setConnectError] = useState<string | null>(null)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [disconnectError, setDisconnectError] = useState<string | null>(null)

  useEffect(() => {
    setViewMode((cur) => {
      if (hasConnections && cur === 'connect') return 'manage'
      if (!hasConnections && cur === 'manage') return 'connect'
      return cur
    })
  }, [hasConnections])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>()

  const onSubmit = async ({ apiKey }: FormValues) => {
    setConnectError(null)
    const isUpdate = viewMode === 'update'
    try {
      await axios.post(
        makeswiftConnectUrl,
        {
          apiKey: apiKey.trim(),
          ...(isUpdate ? { update: true } : {}),
        },
        getAxiosDefaultConfig()
      )
      await retrieveAccounts()
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
        dispatch((fetchAllSecrets as any)({ ownerId }))
      }
      reset()
      setViewMode('manage')
      dispatch((closeConnectionModal as any)())
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        'Failed to connect Makeswift. Please check your API key and try again.'
      setConnectError(msg)
    }
  }

  const disconnect = async () => {
    if (!window.confirm('Disconnect Makeswift and remove the stored API key?')) return
    setDisconnectError(null)
    setIsDisconnecting(true)
    try {
      const row = rows[0]
      // Derive the connector id from the keyId (e.g. "123--makeswift" → "123") so we
      // target the exact row being displayed rather than always hitting /default, which
      // resolves to the session user and can delete the wrong connection on team accounts.
      const connectorId = row?.keyId ? String(row.keyId).split('--')[0]?.trim() || 'default' : 'default'
      const disconnectUrl = `${baseUrl}/auth/makeswift/connections/${connectorId}`
      await axios.delete(disconnectUrl, getAxiosDefaultConfig())
      if (row?.keyId) {
        dispatch((deletePublicKey as any)({ keyId: row.keyId }))
      }
      if (ownerId) {
        dispatch((fetchAllPublicKeys as any)({ ownerId }))
        dispatch((fetchAllSecrets as any)({ ownerId }))
      }
      await retrieveAccounts()
      dispatch((closeConnectionModal as any)())
    } catch (err: any) {
      const msg =
        err?.response?.data?.error || 'Failed to disconnect Makeswift. Please try again.'
      setDisconnectError(msg)
    } finally {
      setIsDisconnecting(false)
    }
  }

  const instructionsBlock = (
    <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4 tw-mb-5 tw-text-sm tw-text-blue-900">
      <p className="tw-mb-2 tw-font-semibold">How to get your Makeswift API key:</p>
      <ol className="tw-list-decimal tw-pl-4 tw-space-y-1">
        <li>
          Open{' '}
          <a
            href="https://app.makeswift.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-blue-600 tw-underline hover:tw-text-blue-800"
          >
            app.makeswift.com
            <FontAwesomeIcon icon={faExternalLinkAlt} className="tw-ml-1 tw-text-xs" />
          </a>{' '}
          and sign in.
        </li>
        <li>
          Create or open your site, then copy the <strong>API key</strong> from your project settings (or the
          Makeswift docs for your setup).
        </li>
      </ol>
    </div>
  )

  const keyForm = (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-mb-4">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-1">
          Makeswift API key
        </label>
        <div className="tw-flex tw-items-center tw-gap-2">
          <input
            {...register('apiKey', {
              required: 'API key is required',
              validate: (v) => v.trim().length > 0 || 'API key cannot be empty',
            })}
            type={showKey ? 'text' : 'password'}
            placeholder="Paste your API key"
            className="tw-flex-1 tw-rounded tw-border tw-border-gray-300 tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-border-blue-400"
          />
          <button
            type="button"
            className="tw-text-gray-500 hover:tw-text-gray-800 tw-text-lg"
            onClick={() => setShowKey((s) => !s)}
            aria-label={showKey ? 'Hide API key' : 'Show API key'}
          >
            {showKey ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>
        {errors.apiKey && (
          <p className="tw-text-red-500 tw-text-xs tw-mt-1">{errors.apiKey.message}</p>
        )}
      </div>

      {connectError && (
        <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-4">
          {connectError}
        </div>
      )}

      <div className="tw-flex tw-items-center tw-justify-end tw-space-x-2">
        <Button
          variant="secondary"
          onClick={() => {
            setConnectError(null)
            reset()
            if (viewMode === 'update') {
              setViewMode('manage')
            } else if (hasConnections) {
              setViewMode('manage')
            } else {
              closeModal()
            }
          }}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-2" />
              {viewMode === 'update' ? 'Updating…' : 'Connecting…'}
            </>
          ) : viewMode === 'update' ? (
            'Save key'
          ) : (
            'Connect'
          )}
        </Button>
      </div>
    </form>
  )

  if (viewMode === 'manage' && hasConnections) {
    const row = rows[0]
    return (
      <div className="tw-p-6">
        <h2 className="tw-text-xl tw-mb-4">Manage Makeswift</h2>

        <div className="tw-p-4 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-mb-6">
          <p className="tw-font-medium tw-text-gray-900">Connected</p>
          <p className="tw-text-sm tw-text-gray-600">
            API key: <span className="tw-font-mono">{row.key_preview || '—'}</span>
          </p>
        </div>

        {disconnectError && (
          <div className="tw-bg-red-50 tw-border tw-border-red-300 tw-rounded tw-px-3 tw-py-2 tw-text-sm tw-text-red-700 tw-mb-4">
            {disconnectError}
          </div>
        )}

        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <div className="tw-flex tw-gap-2">
            <Button
              variant="outline-primary"
              size="sm"
              disabled={isDisconnecting}
              onClick={() => {
                setConnectError(null)
                reset()
                setViewMode('update')
              }}
            >
              Update key
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              disabled={isDisconnecting}
              onClick={() => disconnect()}
            >
              {isDisconnecting ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="tw-mr-1" />
                  …
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
          </div>
          <Button variant="secondary" onClick={closeModal} disabled={isDisconnecting}>
            Close
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="tw-p-6">
      <h2 className="tw-text-xl tw-mb-2">
        {viewMode === 'update' ? 'Update Makeswift API key' : 'Connect Makeswift'}
      </h2>
      {instructionsBlock}
      {keyForm}
    </div>
  )
}
